
import { useState } from 'react';

interface ChecklistItem {
  id: string;
  item: string;
  category: string;
  essential: boolean;
}

export default function PackingChecklist() {
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());
  const [selectedCategory, setSelectedCategory] = useState('all');

  const packingItems: ChecklistItem[] = [
    // Clothing
    { id: 'hiking-boots', item: 'Hiking boots (waterproof)', category: 'clothing', essential: true },
    { id: 'warm-jacket', item: 'Warm jacket/fleece', category: 'clothing', essential: true },
    { id: 'rain-jacket', item: 'Rain jacket/poncho', category: 'clothing', essential: true },
    { id: 'hiking-pants', item: 'Hiking pants (2-3 pairs)', category: 'clothing', essential: false },
    { id: 'thermal-wear', item: 'Thermal underwear', category: 'clothing', essential: true },
    { id: 'hiking-socks', item: 'Hiking socks (wool blend)', category: 'clothing', essential: false },
    { id: 'hat-gloves', item: 'Warm hat and gloves', category: 'clothing', essential: true },
    { id: 'sun-hat', item: 'Sun hat with brim', category: 'clothing', essential: false },
    
    // Gear
    { id: 'backpack', item: 'Daypack (25-30L)', category: 'gear', essential: true },
    { id: 'sleeping-bag', item: 'Sleeping bag (-10°C rating)', category: 'gear', essential: true },
    { id: 'headlamp', item: 'Headlamp + extra batteries', category: 'gear', essential: true },
    { id: 'trekking-poles', item: 'Trekking poles', category: 'gear', essential: false },
    { id: 'water-bottles', item: 'Water bottles (2L capacity)', category: 'gear', essential: true },
    { id: 'sunglasses', item: 'Sunglasses (UV protection)', category: 'gear', essential: true },
    
    // Health & Safety
    { id: 'first-aid', item: 'Personal first aid kit', category: 'health', essential: true },
    { id: 'medications', item: 'Personal medications', category: 'health', essential: true },
    { id: 'altitude-meds', item: 'Altitude sickness medication', category: 'health', essential: false },
    { id: 'sunscreen', item: 'High SPF sunscreen', category: 'health', essential: true },
    { id: 'lip-balm', item: 'Lip balm with SPF', category: 'health', essential: false },
    { id: 'hand-sanitizer', item: 'Hand sanitizer', category: 'health', essential: false },
    
    // Documents & Money
    { id: 'passport', item: 'Passport + visa', category: 'documents', essential: true },
    { id: 'travel-insurance', item: 'Travel insurance documents', category: 'documents', essential: true },
    { id: 'emergency-contacts', item: 'Emergency contact information', category: 'documents', essential: true },
    { id: 'cash-usd', item: 'Cash (USD for tips)', category: 'documents', essential: true },
    { id: 'copies', item: 'Photocopies of important documents', category: 'documents', essential: false },
    
    // Electronics
    { id: 'camera', item: 'Camera + extra batteries/memory', category: 'electronics', essential: false },
    { id: 'phone-charger', item: 'Phone + portable charger', category: 'electronics', essential: false },
    { id: 'power-bank', item: 'Power bank', category: 'electronics', essential: false }
  ];

  const categories = [
    { value: 'all', label: 'All Items', icon: 'ri-list-check-3' },
    { value: 'clothing', label: 'Clothing', icon: 'ri-shirt-line' },
    { value: 'gear', label: 'Gear', icon: 'ri-tools-line' },
    { value: 'health', label: 'Health & Safety', icon: 'ri-first-aid-kit-line' },
    { value: 'documents', label: 'Documents', icon: 'ri-file-text-line' },
    { value: 'electronics', label: 'Electronics', icon: 'ri-smartphone-line' }
  ];

  const filteredItems = selectedCategory === 'all' 
    ? packingItems 
    : packingItems.filter(item => item.category === selectedCategory);

  const essentialItems = filteredItems.filter(item => item.essential);
  const optionalItems = filteredItems.filter(item => !item.essential);

  const toggleItem = (itemId: string) => {
    const newCheckedItems = new Set(checkedItems);
    if (checkedItems.has(itemId)) {
      newCheckedItems.delete(itemId);
    } else {
      newCheckedItems.add(itemId);
    }
    setCheckedItems(newCheckedItems);
  };

  const getProgress = () => {
    const totalItems = filteredItems.length;
    const checkedCount = filteredItems.filter(item => checkedItems.has(item.id)).length;
    return totalItems > 0 ? Math.round((checkedCount / totalItems) * 100) : 0;
  };

  const resetChecklist = () => {
    setCheckedItems(new Set());
  };

  return (
    <div className="bg-white rounded-lg p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <i className="ri-checkbox-line text-[#A67C52]"></i>
          Kilimanjaro Packing Checklist
        </h3>
        <button
          onClick={resetChecklist}
          className="text-xs text-gray-500 hover:text-[#A67C52] transition-colors cursor-pointer px-2 py-1 rounded hover:bg-gray-50"
        >
          Reset All
        </button>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">Progress</span>
          <span className="text-sm text-gray-600">{getProgress()}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-[#A67C52] h-2 rounded-full transition-all duration-300"
            style={{ width: `${getProgress()}%` }}
          ></div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="mb-4">
        <div className="flex flex-wrap gap-2">
          {categories.map(category => (
            <button
              key={category.value}
              onClick={() => setSelectedCategory(category.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-1 ${
                selectedCategory === category.value
                  ? 'bg-[#A67C52] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <i className={category.icon}></i>
              {category.label}
            </button>
          ))}
        </div>
      </div>

      {/* Checklist Items */}
      <div className="space-y-4 max-h-80 overflow-y-auto">
        {essentialItems.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-red-600 mb-2 flex items-center gap-1">
              <i className="ri-star-line"></i>
              Essential Items
            </h4>
            <div className="space-y-2">
              {essentialItems.map(item => (
                <label key={item.id} className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    id={`checklist-${item.id}`}
                    checked={checkedItems.has(item.id)}
                    onChange={() => toggleItem(item.id)}
                    className="w-4 h-4 text-[#A67C52] rounded focus:ring-[#A67C52] border-gray-300 cursor-pointer"
                    aria-label={`Mark ${item.item} as packed`}
                  />
                  <span className={`text-sm flex-1 ${
                    checkedItems.has(item.id) 
                      ? 'line-through text-gray-500' 
                      : 'text-gray-900'
                  }`}>
                    {item.item}
                  </span>
                  <i className="ri-star-fill text-red-400 text-xs" aria-hidden="true"></i>
                </label>
              ))}
            </div>
          </div>
        )}

        {optionalItems.length > 0 && (
          <div>
            <h4 className="text-sm font-semibold text-gray-600 mb-2 flex items-center gap-1">
              <i className="ri-add-circle-line"></i>
              Recommended Items
            </h4>
            <div className="space-y-2">
              {optionalItems.map(item => (
                <label key={item.id} className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    id={`checklist-${item.id}`}
                    checked={checkedItems.has(item.id)}
                    onChange={() => toggleItem(item.id)}
                    className="w-4 h-4 text-[#A67C52] rounded focus:ring-[#A67C52] border-gray-300 cursor-pointer"
                    aria-label={`Mark ${item.item} as packed`}
                  />
                  <span className={`text-sm flex-1 ${
                    checkedItems.has(item.id) 
                      ? 'line-through text-gray-500' 
                      : 'text-gray-900'
                  }`}>
                    {item.item}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 pt-4 border-t border-gray-100">
        <p className="text-xs text-gray-500 text-center">
          <i className="ri-information-line mr-1"></i>
          This checklist is designed for Kilimanjaro trekking. Adjust based on your specific route and season.
        </p>
      </div>
    </div>
  );
}
